<template>
  <el-form :model="form">
    <el-tree
      ref="tree"
      :props="defaultProps"
      :data="data"
      @node-click="handleNodeClick"
      v-bind="$attrs"
      :render-after-expand="false"
    >
      <div class="custom-tree-node" slot-scope="{ node, data }" :class="{ 'none-content': !data.label }">
        <div v-if="node.data.options.showIcon" class="group-icon">
          <div v-for="(icon, idx) in node.data.options.iconClassArray" :key="idx" class="icon">
            <i v-bind:class="icon" />
          </div>
        </div>
        <div class="content" :class="{ 'flex-center': node.level > 1 }">
          <div :class="{ 'full-width': fullWidthContent }">
            <div class="title">
              <slot name="title" v-bind:node="node"></slot>
              <elsa-tag v-if="node.data.options.showTotalItems">
                {{ getTotalItems(node.data.children.length) }}
              </elsa-tag>
              <i
                v-if="!node.isLeaf && node.level == 1"
                v-bind:class="node.expanded ? 'elsa-chevron-circle-up' : 'elsa-chevron-circle-down'"
              />
              <slot v-if="node.data.options.platformAvailability" name="tag-platform-only" v-bind:node="node"></slot>
            </div>
            <div class="subtitle" v-if="node.data.options.showSubTitle">
              <slot name="subtitle" v-bind:node="node"></slot>
            </div>
            <div class="tags" v-if="node.data.options.showTags">
              <slot name="tags" v-bind:node="node"></slot>
            </div>
            <div class="filter" v-if="node.data.options.showFilter && node.expanded">
              <elsa-select
                :label="filterLabel"
                :placeholder="$t('common.filter')"
                :options="filterOptions"
                v-model.lazy="form.filterValue"
                filterable
                @change="() => handleFilterChange(node, data)"
              />
            </div>
            <div class="custom-content" v-if="node.data.options.customContent">
              <slot name="customContent" v-bind:node="node"></slot>
            </div>
          </div>
          <div :class="{ 'flex-center': node.level > 1 }">
            <i
              v-if="!node.isLeaf && node.level > 1"
              class="ml-px-16"
              v-bind:class="node.expanded ? 'elsa-chevron-circle-up' : 'elsa-chevron-circle-down'"
            />
          </div>
        </div>
        <div class="actions-list" v-if="node.data.options.showListAction">
          <el-button
            class="btn btn-remove"
            type="text"
            @click.native="handleAddRemove(node, data, 'remove', $event)"
            v-if="showRemove(node)"
          >
            <font-awesome-icon :icon="['fas', 'trash-alt']" color="#EB5757" />
            {{ node.data.options.removeActionText }}
          </el-button>
          <el-button
            class="btn btn-add"
            type="text"
            @click.native="handleAddRemove(node, data, 'add', $event)"
            v-else-if="!node.data.options.hideAddAction"
          >
            <font-awesome-icon :icon="['fas', 'plus-circle']" />
            {{ node.data.options.addActionText }}
          </el-button>
          <el-button
            class="btn btn-add"
            type="text"
            @click.native="handleAssign(node, data, $event)"
            v-if="node.data.options.assignAction"
          >
            <div class="wrap-assign-btn">
              <chip-icon icon="elsa-icon-paper-airplane"/>
              {{ node.data.options.asignActionText }}
            </div>
          </el-button>
        </div>
        <div class="actions-list-custom" v-if="node.data.options.showEditAction">
          <el-tooltip class="item" :content="node.data.options.editActionText" placement="top" :visible-arrow="false">
            <tertiary-button
              class="btn-edit"
              :class="{ 'btn-edit-disabled': node.data.options.editDisabled }"
              @click.native="handleEdit(node, data, $event)"
            >
              <font-awesome-icon icon="pen" />
            </tertiary-button>
          </el-tooltip>
        </div>
      </div>
    </el-tree>
    <confirm-modal
      :dialogVisible="showConfirmModal"
      append-to-body
      :descModal="descModal"
      :nodeModal="nodeModal"
      v-on:closed="showConfirmModal = false"
      v-on:continute="processAddRemove"
    />
  </el-form>
</template>

<script>
/*
  props:
    - data:
      - @description: tree data
      - @example:
        [{
          "id": Number,
          "label": String,
          "children": [], // loop of data props
          "options": {
              "showSubTitle": Boolean,
              "showListAction": Boolean,
              "hideAddAction": Boolean,
              "removeNode": Boolean,
              "showTags": Boolean,
              "showFilter": Boolean,
              "showIcon": Boolean,
              "iconClassArray": [],
              "removeActionText": String,
              "addActionText": String,
          }
        }]
    - form:
      - @description: filter selected data
      - @example:
        {
          filterValue: Number
        }
    - filterOptions:
      - @description: filter dropdown data
      - @example:
        [{
          label: String,
          value: Number
        }]
    - selectedItems:
      - @description: array of keys of initially checked nodes group by node level
      - @example:
        {
          `level-${node.level}`: [Number]
        }
  methods:
    - handleNodeClick: triggers when a node is clicked
    - handleFilterChange: triggers when a filter dropdown is changed
    - handleAddRemove: triggers when button add/remove is clicked
    - showRemove: whether to show remove button when a node has been selected
 */
import ElsaSelect from "@/components/common/Select";
import ElsaTag from "@/components/common/Tag";
import ConfirmModal from "@/components/modals/homeworks/ConfirmModal";
import TertiaryButton from "@/components/common/buttons/TertiaryButton";
import ChipIcon from "@/components/common/ChipIcon";

export default {
  name: "ElsaTree",
  components: {
    ElsaSelect,
    ConfirmModal,
    TertiaryButton,
    ElsaTag,
    ChipIcon,
  },
  data() {
    return {
      defaultProps: {
        children: "children",
        label: "label",
      },
      showConfirmModal: false,
      nodeModal: null,
      descModal: "",
    };
  },
  mounted() {
    this.hideNoneContent();
  },
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    form: {
      type: Object,
      default: () => ({
        filterValue: undefined,
      }),
    },
    // @todo: data example [{ label, value }]
    filterOptions: {
      type: Array,
      default: () => [],
    },
    selectedItems: {
      type: Object,
      default: () => {},
    },
    filterLabel: {
      type: String,
      default: "",
    },
    fullWidthContent: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    filterOptions: {
      handler(val) {
        if (val.length > 0) {
          this.form.filterValue = val[0].value;
        }
      },
      immediate: true,
    },
  },
  methods: {
    async hideNoneContent() {
      await this.$nextTick();
      const $noneData = this.$refs.tree.$el.getElementsByClassName("none-content");
      if (!$noneData.length) return;

      $noneData.forEach(el => {
        el.parentElement.style.display = "none";
        const parent = el.parentElement.parentElement;
        const classList = parent.classList;
        const isExpend = classList.contains("is-expanded");
        if (!isExpend) {
          parent.click();
        }
      });
    },
    handleNodeClick(data, node) {
      this.$emit("node-click", data, node);
    },

    handleFilterChange(node, data) {
      this.$emit("filter-change", node, data);
    },

    handleAddRemove(node, data, action, event) {
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }
      if (node.data.options.showConfirm) {
        this.showConfirmModal = true;
        this.nodeModal = node;
        this.getDescModal(node);
      } else {
        this.processAddRemove(node, data, action);
      }
    },
    handleAssign(node, data, event) {
      event.stopPropagation();
      event.preventDefault();
      this.$emit("assign", node);
    },

    processAddRemove(node, data, action) {
      this.$emit("add-remove", node);
      if (action === "remove" && node.data.options.removeNode) {
        this.removeNode(node, data);
      }
    },

    showRemove(node) {
      const parentLevelItems = this.selectedItems && this.selectedItems[`level-${node.level}`];
      const allParentSelected = parentLevelItems && parentLevelItems.includes(node.data.original_id);

      const selectedChild = this.selectedItems && this.selectedItems[`level-${node.level + 1}`];
      const allChildSelected =
        selectedChild &&
        selectedChild.length &&
        node.data.children.every(child => selectedChild.includes(child.original_id));

      return allParentSelected || allChildSelected;
    },

    removeNode(node, data) {
      const parent = node.parent;
      const children = parent.data.children || parent.data;
      let index = children.findIndex(d => d.id === data.id);
      // console.log('removeNode', index, data.id, node)
      children.splice(index, 1);

      if (children.length === 0) {
        // console.log('removeNode2', index, children.length, parent.id)
        this.removeNode(parent, parent.data);
      }
    },

    getDescModal(node) {
      if (node.level == 3) {
        this.descModal = this.$t("homework.modals.delete_all");
      } else if (node.level == 4) {
        this.descModal = this.$t("homework.modals.delete");
      }
    },

    handleEdit(node, data, event) {
      event.stopPropagation();
      event.preventDefault();
      this.$emit("action-edit", data, node);
    },

    getTotalItems(total) {
      const text = total > 1 ? this.$t("common.items") : this.$t("common.item");

      return `${total} ${text}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.custom-tree-node {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-left: 16px;

  .content {
    flex: 1;
  }

  .title {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .filter {
    display: flex;
    margin-top: 16px;
  }

  span {
    font-family: SF Pro Display, sans-serif;
    font-style: normal;
    font-weight: normal;
  }

  .root-title {
    font-weight: 600;
    font-size: 20px;
    line-height: 20px;
    text-transform: uppercase;
    letter-spacing: 0.25px;
    color: #121131;
  }

  .subtitle {
    font-size: 14px;
    line-height: 20px;
    text-transform: uppercase;
    color: #8e8e93;
  }

  .level-1-title {
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.25px;
    color: #121131;
  }

  .level-2-title {
    font-size: 14px;
    line-height: 24px;
    letter-spacing: 0.25px;
    color: #121131;
  }

  button {
    font-family: SF Pro Display, sans-serif;
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 24px;
    letter-spacing: 0.25px;
    color: #007eff;
  }

  .btn {
    border-radius: 4px;
    height: 32px;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 0 10px;
  }

  .elsa-level-down {
    width: 15px;
    height: 20px;
    margin-right: 16px;
    margin-top: 10px;
  }

  .elsa-grip-lines {
    width: 18px;
    height: 20px;
    margin-right: 16px;
    margin-top: 10px;
  }

  .group-icon {
    display: flex;
    align-items: center;
    margin-right: 16px;
  }

  .custom-content {
    padding-left: 50px;
  }

  .flex-center {
    display: flex;
    align-items: center;
  }

  .tags,
  .subtitle {
    padding-top: 4px;
  }

  .wrap-assign-btn {
    display: flex;
    align-items: center;
    gap: 10px;


    ::v-deep {
      button {
        &:hover {
          margin: 0;
        }
      }
    }
  }
}
.full-width {
  width: 100%;
}
::v-deep {
  .el-tree-node {
    border-bottom: 1px solid #e5e5ea;
    &:last-child {
      border: none;
    }

    .el-tree-node__content {
      padding: 24px 32px;
      height: auto;
    }
  }

  .el-tree {
    background: #ffffff;
    border: 1px solid #e5e5ea;
    box-sizing: border-box;
    border-radius: 12px;
    overflow: hidden;
  }

  .el-tree-node .el-tree-node .el-tree-node {
    background: #fafafc;
  }

  .el-form-item.elsa-input {
    min-width: 370px;
    margin-bottom: 0;
  }

  .el-button--text {
    span {
      font-weight: 600;
      font-size: 14px;
      line-height: 24px;
    }
  }

  .btn-add,
  .btn-preview {
    background: rgba(0, 126, 255, 0.1) !important;
    span {
      color: #007eff;
    }
  }
  .btn-preview {
    text-decoration: none !important;
    padding: 0 0px 0 8px !important;
    color: #007eff;
    i {
      margin-left: 10px !important;
    }
  }
  button.elsa-chip {
    margin: 0;
    i.elsa-icon-paper-airplane {
      &::before {
        height: 16px;
        width: 16px;
      }
    }
  }

  .btn-add:hover,
  .btn-preview:hover {
    background: rgba(0, 126, 255, 0.1);
    span:hover {
      color: #007eff;
    }
  }

  .btn-add:active,
  .btn-preview:active {
    background: rgba(0, 126, 255, 0.1);
  }

  .btn-remove {
    background: rgba(235, 87, 87, 0.1) !important;
    span {
      color: #eb5757;
    }
  }

  .btn-remove:hover {
    background: rgba(235, 87, 87, 0.1);
    span:hover {
      color: #eb5757;
    }
  }

  .btn-remove:active {
    background: rgba(235, 87, 87, 0.1);
  }

  .btn-edit-disabled {
    color: #c0c4cc !important;
    background-image: none;
    background-color: #fff;
    border-color: #ebeef5;

    &:active,
    &:hover {
      border-color: #ebeef5 !important;
    }
  }

  .btn-edit {
    height: 48px !important;
    width: 48px;
    border: 1px solid #e5e5ea;
    min-width: 48px;

    span {
      font-weight: 300;
      font-size: 16px;
      cursor: pointer;
    }
  }

  .level-3-title {
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0.25px;
    color: #121131;
    margin-left: 80px;
  }

  .el-tree-node > .el-tree-node__children {
    border-top: 1px solid #e5e5ea;
  }
}
</style>
